import React, { useState, useEffect } from "react";
import Grid from "../Grid/Grid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    Box,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Tooltip
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ScaleIcon from '@mui/icons-material/Scale';
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import MizanUnitsSearch from "./MizanUnitsSearch";
import mizanUnitAPI from "../../Network/MizanUnit";

const cookies = new Cookies();

export default function MizanUnits() {
    const [t] = useTranslation("common");
    const [rows, setRows] = useState([]);
    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);
    const [loading, setLoading] = useState(false);
    const maintheme = useSelector((state) => state.themeData.maintheme);

    const [pageCount, setPageCount] = useState(0);
    const [openFilterColumn, setOpenFilterColumn] = useState(false);
    const [openFilterColumnValue, setOpenFilterColumnValue] = useState([]);
    const [elementClicked, setElementClicked] = useState(null);

    // Mizan Details Dialog
    const [mizanDialogOpen, setMizanDialogOpen] = useState(false);
    const [mizanDetails, setMizanDetails] = useState([]);
    const [loadingMizanDetails, setLoadingMizanDetails] = useState(false);
    const [selectedUnitName, setSelectedUnitName] = useState("");

    // Lookups
    const [lookups, setLookups] = useState({
        unit_types: [],
        mizans: [],
        belongings: [],
        lifecycles: []
    });
    const [loadingLookups, setLoadingLookups] = useState(true);

    // Filters
    const [filters, setFilters] = useState({
        name: "",
        barcode: "",
        tag_code: "",
        unit_type_id: null,
        mizan_id: null,
        belonging_id: null,
        lifecycle_id: null,
        municipality_id: null,
        contractor_id: null,
        mizan_account_id: null,
        is_governmental: "",
        lifecycle_mode: "",
        capacity: "",
        export_excel: false,
        unit_type_selected: null,
        mizan_selected: null,
        belonging_selected: null,
        lifecycle_selected: null,
        municipality_selected: null,
        contractor_selected: null,
        account_selected: null
    });
    const [reload, setReload] = useState(false);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage + 1);
    };

    const navigate = useNavigate();
    const controller = new AbortController();
    const [rowsTotal, setRowsTotal] = useState(0);
    const screenwidth = useSelector((state) => state.settingsData.screenwidth);

    // Get sync status color
    const getSyncStatusColor = (status) => {
        switch (status) {
            case 'synced': return 'success';
            case 'pending': return 'warning';
            case 'error': return 'error';
            case 'duplicate': return 'info';
            default: return 'default';
        }
    };

    // Handle mizan count click to show popup
    const handleMizanCountClick = async (row) => {
        if (!row?.id || !row?.mizan_count || row.mizan_count <= 0) {
            toast.info(t("MIZAN_UNITS.no_mizan_data") || "لا توجد بيانات ميزان");
            return;
        }

        setSelectedUnitName(row?.name || "");
        setMizanDialogOpen(true);
        setLoadingMizanDetails(true);

        try {
            const result = await mizanUnitAPI.getMizanDetails({
                unitId: row.id,
                params: headerObject
            });

            if (result.status && result?.data?.data) {
                setMizanDetails(result.data.data);
            } else {
                setMizanDetails([]);
            }
        } catch (error) {
            console.error('Error loading mizan details:', error);
            setMizanDetails([]);
        } finally {
            setLoadingMizanDetails(false);
        }
    };

    const columns = [
        { field: "index_column", headerName: "ت", minWidth: 40, maxWidth: 50, width: 50 },
        {
            field: "id",
            headerName: t("MIZAN_UNITS.unit_id") || "معرف المركبة",
            minWidth: 90,
            maxWidth: 110,
            renderCell: (params) => (
                <Tooltip title={t("MIZAN_UNITS.unit_id") || "معرف المركبة"} arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1565c0' }}>
                            {String(params.value || 0).padStart(5, '0')}
                        </Typography>
                        <LocalShippingIcon sx={{ fontSize: 16, color: '#1565c0 !important' }} />
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "name",
            headerName: t("MIZAN_UNITS.unit_name") || "اسم المركبة / الباركود",
            minWidth: 250,
            flex: 2.5,
            renderCell: (params) => (
                <Tooltip title={`${t("MIZAN_UNITS.unit_name") || "اسم المركبة"}: ${params.value || "غير معرف"} | ${t("MIZAN_UNITS.barcode") || "الباركود"}: ${params.row?.barcode || "غير معرف"} | ${t("MIZAN_UNITS.tag_code") || "رمز الملصق"}: ${params.row?.tag_code || "غير معرف"}`} arrow>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, width: '100%' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                            {params.value || "غير معرف"}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 300, color: '#757575', flex: 1 }}>
                                {params.row?.barcode || "غير معرف"} {params.row?.tag_code && `- ${params.row.tag_code}`}
                            </Typography>
                            <QrCodeIcon sx={{ fontSize: 18, color: '#424242 !important' }} />
                        </Box>
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "municipality_name",
            headerName: t("MIZAN_UNITS.municipality") || "البلدية / المتعهد",
            minWidth: 160,
            flex: 1.6,
            renderCell: (params) => (
                <Tooltip title={`${t("MIZAN_UNITS.municipality") || "البلدية"}: ${params.value || "غير معرف"} | ${t("MIZAN_UNITS.contractor") || "المتعهد"}: ${params.row?.contractor_name || "غير معرف"}`} arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>
                                {params.value || "غير معرف"}
                            </Typography>
                            <Typography sx={{ fontSize: '11px', fontWeight: 300, color: '#757575' }}>
                                {params.row?.contractor_name || "غير معرف"}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            <LocationCityIcon sx={{ fontSize: 16, color: '#1976d2 !important' }} />
                            <BusinessIcon sx={{ fontSize: 16, color: '#f57c00 !important' }} />
                        </Box>
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "account_name",
            headerName: t("MIZAN_UNITS.account_name") || "اسم الحساب",
            minWidth: 180,
            flex: 1.8,
            renderCell: (params) => (
                <Tooltip title={`${t("MIZAN_UNITS.account_name") || "اسم الحساب"}: ${params.value || "غير معرف"} | ${t("MIZAN_UNITS.days_counter") || "الايام المتبقية"}: ${params.row?.days_counter || 0}`} arrow>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{params.value || "غير معرف"}</Typography>
                            <AccountBalanceIcon sx={{ fontSize: 18, color: '#2e7d32 !important' }} />
                        </Box>
                        {params.row?.days_counter !== null && params.row?.days_counter !== undefined && (
                            <Typography sx={{ fontSize: '10px', color: '#757575' }}>
                                {t("MIZAN_UNITS.days_counter") || "الايام المتبقية"}: {params.row.days_counter}
                            </Typography>
                        )}
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "is_governmental",
            headerName: t("MIZAN_UNITS.unit_info") || "معلومات المركبة",
            minWidth: 170,
            flex: 1.8,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
                    <Tooltip title={t("MIZAN_UNITS.is_governmental") || "نوع الجهد"} arrow>
                        <Chip
                            label={params.row?.is_governmental === 1 ?
                                (t("MIZAN_UNITS.governmental_owned") || "جهد ذاتي") :
                                (t("MIZAN_UNITS.governmental_rented") || "جهد مؤجر")}
                            size="small"
                            sx={{
                                width: '100%',
                                height: 20,
                                fontSize: '10px',
                                backgroundColor: params.row?.is_governmental === 1 ? '#4caf50' : '#ff9800',
                                color: '#fff',
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 1 }
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={t("MIZAN_UNITS.unit_type") || "نوع المركبة"} arrow>
                        <Chip
                            label={params.row?.unit_type_name || "غير معرف"}
                            size="small"
                            variant="outlined"
                            sx={{
                                width: '100%',
                                height: 20,
                                fontSize: '10px',
                                borderColor: '#2196f3',
                                color: '#2196f3',
                                '& .MuiChip-label': { px: 1 }
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={t("MIZAN_UNITS.lifecycle_mode") || "نوع العمل"} arrow>
                        <Chip
                            label={params.row?.lifecycle_mode || "غير معرف"}
                            size="small"
                            sx={{
                                width: '100%',
                                height: 20,
                                fontSize: '10px',
                                backgroundColor: '#e0e0e0',
                                color: '#424242',
                                '& .MuiChip-label': { px: 1 }
                            }}
                        />
                    </Tooltip>
                </Box>
            )
        },
        {
            field: "capacity",
            headerName: t("MIZAN_UNITS.weight_info") || "معلومات الوزن",
            minWidth: 180,
            flex: 1.8,
            renderCell: (params) => (
                <Tooltip
                    title={
                        `${t("MIZAN_UNITS.net_weight_limit") || "أقل وزن حمولة"}: ${parseInt(params.row?.net_weight_limit) || 0}\n` +
                        `${t("MIZAN_UNITS.empty_weight") || "الوزن الفارغ"}: ${parseInt(params.row?.weight) || 0}\n` +
                        `${t("MIZAN_UNITS.capacity") || "السعة"}: ${parseInt(params.row?.capacity) || 0}`
                    }
                    arrow
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, flex: 1 }}>
                            <Typography sx={{ fontSize: '10px', color: '#616161' }}>
                                {parseInt(params.row?.net_weight_limit) || 0}
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: '#616161' }}>
                                {parseInt(params.row?.weight) || 0}
                            </Typography>
                            <Typography sx={{ fontSize: '10px', color: '#616161' }}>
                                {parseInt(params.row?.capacity) || 0}
                            </Typography>
                        </Box>
                        <ScaleIcon sx={{ fontSize: 22, color: '#7b1fa2 !important' }} />
                    </Box>
                </Tooltip>
            )
        },
        {
            field: "mizan_count",
            headerName: t("MIZAN_UNITS.mizans") || "الموازين",
            minWidth: 110,
            flex: 1,
            renderCell: (params) => {
                const count = params.value || 0;
                return (
                    <Tooltip title={`${t("MIZAN_UNITS.mizans") || "الموازين"}: ${count}`} arrow>
                        <Chip
                            label={`${count} ${t("MIZAN_UNITS.mizan")}`}
                            size="small"
                            color={count > 0 ? "primary" : "default"}
                            variant={count > 0 ? "filled" : "outlined"}
                            onClick={() => count > 0 && handleMizanCountClick(params.row)}
                            sx={{
                                cursor: count > 0 ? 'pointer' : 'default',
                                fontWeight: count > 0 ? 600 : 400,
                                '&:hover': count > 0 ? {
                                    backgroundColor: maintheme?.primary,
                                    color: '#fff',
                                    transform: 'scale(1.05)',
                                    transition: 'all 0.2s'
                                } : {}
                            }}
                        />
                    </Tooltip>
                );
            }
        },
    ];

    const headerObject = {
        username: cookies.get("user_name") || null,
        user_id: cookies.get("user_id") || null,
        account_name: cookies.get("account_name") || null,
        account_id: cookies.get("account_id") || null,
        authHash: cookies.get("authHash") || null,
        sid: cookies.get("sid") || null,
        baseurl: cookies.get("baseUrl") || null,
        id: cookies.get("id") || null,
    };

    // Load lookups on mount
    const loadLookups = async () => {
        try {
            const result = await mizanUnitAPI.getLookups({
                params: headerObject,
                signal: controller?.signal,
            });

            if (result.status && result?.data?.data) {
                setLookups(result.data.data);
            }
        } catch (error) {
            console.error('Error loading lookups:', error);
        } finally {
            setLoadingLookups(false);
        }
    };

    useEffect(() => {
        loadLookups();
        // Trigger initial data load after component mounts
        // const timer = setTimeout(() => {
        // loadData();
        // }, 100);
        // return () => {
        //     controller.abort();
        //     clearTimeout(timer);
        // };
    }, []);

    useEffect(() => {
        // loadLookups();
        loadData();
    }, [pageNumber, pageSize, reload]);

    const loadData = async () => {
        const params = {
            ...headerObject,
            ...(filters?.export_excel ? {} : {
                page: pageNumber,
                page_size: pageSize,
            }),
            name: filters?.name,
            barcode: filters?.barcode,
            tag_code: filters?.tag_code,
            unit_type_id: filters?.unit_type_id,
            mizan_id: filters?.mizan_id,
            belonging_id: filters?.belonging_id,
            lifecycle_id: filters?.lifecycle_id,
            municipality_id: filters?.municipality_id,
            contractor_id: filters?.contractor_id,
            mizan_account_id: filters?.mizan_account_id,
            is_governmental: filters?.is_governmental,
            lifecycle_mode: filters?.lifecycle_mode,
            capacity: filters?.capacity,
            export_excel: filters?.export_excel,
        };

        setLoading(true);

        try {
            const result = await mizanUnitAPI.getMizanUnits({
                params,
                signal: controller?.signal,
            });

            if (result.status && result?.data?.data) {
                if (filters?.export_excel) {
                    setFilters({ ...filters, export_excel: false });
                    downloadExcel(result?.data?.data);
                } else {
                    if (result?.data?.data?.data?.length > 0) {
                        setRows(result.data.data.data);
                        setRowsTotal(result.data.data.total_records);
                        setPageCount(Math.ceil(result.data.data.total_records / pageSize));
                    } else {
                        setRows([]);
                        setRowsTotal(0);
                        setPageCount(0);
                    }
                }
            } else {
                if (result?.data?.data?.message) {
                    toast.error(result.data.data.message?.text || result.data.data.message);
                }
                if (result?.data?.data?.message?.text === "unauthorized to complete") {
                    navigate("/redirect");
                }
            }
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error(t("NETWORK_MESSAGES.unknownError"));
        } finally {
            setLoading(false);
        }
    };

    const downloadExcel = async (fileName) => {
        try {
            const result = await mizanUnitAPI.downloadExcel({
                params: { ...headerObject, fileName }
            });

            if (result.status && result?.data) {
                const url = window.URL.createObjectURL(result.data);
                window.open(url, "_blank")?.focus();
            }
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error('Error downloading file');
        }
    };

    const clearFilterFunction = () => {
        setFilters({
            name: "",
            barcode: "",
            tag_code: "",
            unit_type_id: null,
            mizan_id: null,
            belonging_id: null,
            lifecycle_id: null,
            municipality_id: null,
            contractor_id: null,
            mizan_account_id: null,
            is_governmental: "",
            lifecycle_mode: "",
            capacity: "",
            export_excel: false,
            unit_type_selected: null,
            mizan_selected: null,
            belonging_selected: null,
            lifecycle_selected: null,
            municipality_selected: null,
            contractor_selected: null,
            account_selected: null
        });
    };

    const searchFunction = () => {
        setReload(!reload);
        setPageNumber(1);
    };

    const exportFunction = () => {
        setFilters({ ...filters, export_excel: true });
        setReload(!reload);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                justifyContent: "center",
                alignItems: "start",
                overflowX: "hidden",
                "& .MuiChip-label": {
                    fontFamily: "Cairo-Medium",
                },
            }}
        >
            {/* Title Header for spacing */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    height: "60px",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontFamily: "Cairo-Bold",
                        fontSize: "20px",
                        letterSpacing: "-1px",
                    }}
                >
                    {t("MIZAN_UNITS.title")}
                </Typography>
            </Box>

            <Grid
                rows={rows}
                hasExport={true}
                exportFunction={exportFunction}
                columns={columns}
                pageSize={pageSize}
                setPageSize={setPageSize}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                loading={loading}
                handlePageChange={handlePageChange}
                rowsTotal={rowsTotal}
                checkScreenSize={screenwidth}
                pageCount={pageCount}
                openFilterColumn={openFilterColumn}
                elementClicked={elementClicked}
                setOpenFilterColumn={setOpenFilterColumn}
                setColumnFilterValue={setOpenFilterColumnValue}
                columFilterValue={openFilterColumnValue}
                clearFunction={clearFilterFunction}
                searchFunction={searchFunction}
                hasCreate={false}
                filterChilds={
                    <MizanUnitsSearch
                        filters={filters}
                        setFilters={setFilters}
                        lookups={lookups}
                        loadingLookups={loadingLookups}
                    />
                }
            />

            {/* Mizan Details Dialog */}
            <Dialog
                open={mizanDialogOpen}
                onClose={() => setMizanDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: maintheme?.primary,
                    color: '#fff',
                    fontFamily: 'Cairo-Bold'
                }}>
                    <Typography sx={{ fontFamily: 'Cairo-Bold', fontSize: '18px' }}>
                        {t("MIZAN_UNITS.mizan_details") || "تفاصيل الميزان"} - {selectedUnitName}
                    </Typography>
                    <IconButton onClick={() => setMizanDialogOpen(false)} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {loadingMizanDetails ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : mizanDetails.length > 0 ? (
                        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontFamily: 'Cairo-Bold', textAlign: 'right' }}>
                                            {t("MIZAN_UNITS.mizan") || "الميزان"}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'Cairo-Bold', textAlign: 'right' }}>
                                            {t("MIZAN_UNITS.belonging") || "مجاميع الميزان"}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'Cairo-Bold', textAlign: 'right' }}>
                                            {t("MIZAN_UNITS.lifecycle") || "دورة الحياة"}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'Cairo-Bold', textAlign: 'right' }}>
                                            {t("MIZAN_UNITS.wialon_user") || "مستخدم المنصة"}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mizanDetails.map((detail, index) => (
                                        <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                            <TableCell sx={{ fontFamily: 'Cairo-Medium', textAlign: 'right' }}>
                                                {detail.mizan_name || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'Cairo-Medium', textAlign: 'right' }}>
                                                {detail.belonging_name || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'Cairo-Medium', textAlign: 'right' }}>
                                                {detail.lifecycle_name || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'Cairo-Medium', textAlign: 'right' }}>
                                                {detail.wialon_user || '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontFamily: 'Cairo-Medium', color: '#666' }}>
                                {t("MIZAN_UNITS.no_mizan_data") || "لا توجد بيانات ميزان"}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
